import React from "react";
import { useTranslation } from "react-i18next";
import { OperationalShell } from "../components/OperationalShell";
import { TileGrid } from "../components/TileGrid";
import { ActivityPulse } from "../components/ActivityPulse";
import { useCapability } from "../useCapability";
import { TILES_IN_ORDER } from "../tiles";
import { OperationalBrandHeader } from "../components/OperationalBrandHeader";
import { useDemoMode } from "../demoMode/DemoModeContext";

/**
 * Operational Base Home — Phase 2 surface.
 *
 * Hierarchy on the page (top-to-bottom):
 *   1. Welcome — single sentence, no widgets, no clutter
 *   2. Adaptive tile grid — max 6 visible tiles, auto-flowing (Binding Rule #7)
 *   3. Activity Pulse — calm plain-language feed (light Phase 2 introduction)
 *
 * Every visibility decision on this page reads from the capability map.
 * No `if role === ...` checks live here. The orchestration brain decides
 * what's present; this page just lays out what's allowed.
 *
 * 5-second comprehension test (Binding Rule #3):
 *   * Operator opens the page
 *   * Sees their org/location context (breadcrumb in shell)
 *   * Sees up to 6 operational tiles, each with one metric + one context
 *   * Sees what changed recently in plain language
 * They should leave the page knowing the operational state without
 * scrolling, reading, or interpreting charts.
 */
const OperationalBaseHome = () => {
    const { t } = useTranslation();
    const { map, loading } = useCapability();
    const { active: demoActive } = useDemoMode();

    // Honor Binding Rule #7 — max 6 tiles on the home view. Modules beyond
    // that remain in the sidebar (and become tiles on the future "All
    // modules" surface in Phase 5).
    const HOME_TILE_LIMIT = 6;
    const visibleNames = new Set((map?.visible_modules || []).map((m) => m.name));

    // Build the rendered tile list — each tile self-hides via ModuleBoundary,
    // but to honor the 6-tile rule we slice based on capability before rendering.
    const tilesToRender = [];
    for (const TileFn of TILES_IN_ORDER) {
        // The tile component knows its own moduleName via ModuleBoundary.
        // We can't introspect it without rendering, but the tiles list is
        // ordered identically to the priority modules below — so we walk
        // the visible_modules in tile_order and render only the matching
        // tile components.
        if (tilesToRender.length >= HOME_TILE_LIMIT) break;
        tilesToRender.push(TileFn);
    }

    // Filter by visibility before rendering (avoids rendering tiles whose
    // ModuleBoundary will return null, which would still occupy a slot count
    // pre-mount). We trust the per-tile `moduleName` declared in tiles/index.
    const TILE_MODULE_MAP = [
        ["review_shield",  TILES_IN_ORDER[0]],
        ["review_shield",  TILES_IN_ORDER[12]],   // Review Shield Intelligence (Phase 8.1)
        ["surveys",        TILES_IN_ORDER[1]],
        ["vouchers",       TILES_IN_ORDER[2]],
        ["recovery",       TILES_IN_ORDER[3]],
        ["analytics",      TILES_IN_ORDER[4]],
        ["locations",      TILES_IN_ORDER[5]],
        ["branding",       TILES_IN_ORDER[6]],
        ["notifications",  TILES_IN_ORDER[7]],
        ["permissions",    TILES_IN_ORDER[8]],
    ];
    // Phase 3: surface the Modules tile to super-admins only — the rest
    // of the org should never see governance plumbing on their home grid.
    if (map?.role === "super_admin") {
        TILE_MODULE_MAP.unshift(["operational_base", TILES_IN_ORDER[9]]);
    }
    const visibleTiles = TILE_MODULE_MAP
        .filter(([name]) => visibleNames.has(name))
        .slice(0, HOME_TILE_LIMIT);

    return (
        <OperationalShell>
            {/* Welcome — brand logo (compact) sits to the LEFT of the title,
                the Show-Me button stays anchored top-right. */}
            <header
                className="mb-10 lg:mb-14 flex items-center justify-between gap-6 flex-wrap"
                data-testid="ob-home-header"
            >
                <div className="flex items-center gap-6 sm:gap-8 min-w-0 flex-1">
                    <OperationalBrandHeader
                        variant="showcase"
                        size="compact"
                        requireFallback={true}
                    />
                    <div className="space-y-3 min-w-0">
                        <p className="ob-eyebrow">{t('operationalBase.today')}</p>
                        <h1
                            className="text-4xl sm:text-5xl"
                            style={{ color: "var(--ob-text-primary)" }}
                            data-testid="ob-home-title"
                        >
                            Your operational base.
                        </h1>
                        <p
                            className="text-base max-w-2xl"
                            style={{ color: "var(--ob-text-secondary)" }}
                            data-testid="ob-home-subtitle"
                        >
                            {greetingForRole(map?.role)}
                        </p>
                    </div>
                </div>
                {/* v4.8.2 — Guided Demo hero CTA removed. The Guided
                    Demo is now an opt-in presentation layer launched
                    exclusively from inside Master Demo Mode. */}
            </header>

            {/* Adaptive tile grid */}
            {loading && (
                <p
                    data-testid="ob-home-loading"
                    className="text-sm"
                    style={{ color: "var(--ob-text-tertiary)" }}
                >
                    Loading your environment…
                </p>
            )}

            {!loading && visibleTiles.length === 0 && (
                <section
                    data-testid="ob-home-empty"
                    className="ob-card p-8 text-center"
                >
                    <p
                        className="text-base"
                        style={{ color: "var(--ob-text-secondary)" }}
                    >
                        Nothing has been enabled for you yet — your shell is calm.
                    </p>
                </section>
            )}

            {!loading && visibleTiles.length > 0 && (
                <TileGrid data-testid="ob-home-tile-grid">
                    {visibleTiles.map(([name, TileFn], idx) => (
                        <TileFn key={`${name}-${idx}`} />
                    ))}
                </TileGrid>
            )}

            {/* Activity Pulse — light Phase 2 surface.
                v4.8.1 — hidden while Master Demo Mode is active to keep
                the curated investor flow free of real-tenant events. */}
            {!demoActive && (
                <div className="mt-10 lg:mt-14" data-testid="ob-home-activity-zone">
                    <ActivityPulse limit={6} />
                </div>
            )}
        </OperationalShell>
    );
};

function greetingForRole(role) {
    // Plain-language stance per role (§4 role philosophy).
    if (role === "super_admin") {
        return "The platform is steady. Choose a module to inspect, or watch what's changed below.";
    }
    if (role === "manager") {
        return "Just the work that needs your attention today.";
    }
    if (role === "client_admin" || role === "enterprise_admin") {
        return "Aerial view of your organization. Drill in where it matters.";
    }
    return "Welcome back.";
}

export default OperationalBaseHome;
